import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';

import { isPresent } from './data';

dayjs.extend(utc);

export enum TRepetitionActionType {
  skip = 'skip',
  reset = 'reset',
  shift = 'shift',
}
export type TRepetitionAction =
  | { type: TRepetitionActionType.skip }
  | { type: TRepetitionActionType.reset }
  | { type: TRepetitionActionType.shift; factor: number };

const FACTORS = {
  BAD: 0.5,
  GOOD: 2,
  PERFECT: 4,
  INIT: -1,
} as const;

const INIT_SHIFT_IN_HOURS: Record<
  // TODO: completely refactor
  'passive' | 'active',
  Record<TExercise, number>
> = {
  active: {
    [TExercise.wordCards]: 3,
    [TExercise.wordCardsReverse]: 9,
  },
  passive: {
    [TExercise.wordCards]: 3,
    [TExercise.wordCardsReverse]: 3,
  },
};
const RESET_SHIFT_IN_HOURS = 0;
const MAX_EXPIRATION_SHIFT = 0.3;
const EXPIRATION_FACTOR = 0.5;
const MIN_DISTANCE_IN_HOURS = INIT_SHIFT_IN_HOURS;

const roundFloat = (v: number) => _.round(v, 3);

export const getActionsByWord = (
  word: TWord,
  exercise: TExercise
): TRepetitionAction[] => {
  const status = word.status[exercise];
  const isReseted = calcDistanceInHours(status) === 0;

  const reset: TRepetitionAction = { type: TRepetitionActionType.reset };
  const skip: TRepetitionAction = { type: TRepetitionActionType.skip };
  const badCase = isReseted
    ? undefined
    : { type: TRepetitionActionType.shift, factor: FACTORS.BAD };
  const goodCase = isReseted
    ? { type: TRepetitionActionType.shift, factor: FACTORS.INIT }
    : { type: TRepetitionActionType.shift, factor: FACTORS.GOOD };
  const perfectCase = isReseted
    ? undefined
    : { type: TRepetitionActionType.shift, factor: FACTORS.PERFECT };

  return [reset, skip, badCase, goodCase, perfectCase].filter(isPresent);
};

const now = () => dayjs.utc();

const calcDistanceInHours = (info: TExerciseStatus | undefined): number => {
  if (!info) return 0;

  const { lastProbeAt, nextProbeAt } = info;
  const periodH = dayjs.utc(nextProbeAt).diff(dayjs.utc(lastProbeAt), 'hour');
  const expirationH = now().diff(dayjs.utc(nextProbeAt), 'hour');
  const expirationShiftH = Math.min(
    periodH * MAX_EXPIRATION_SHIFT,
    Math.abs(expirationH) * EXPIRATION_FACTOR
  );
  const sign = expirationH > 0 ? -1 : +1;

  /**
   * 2 cases:
   *
   * 1. expirationH > 0, that means that it's time to repeat. E.g.:
   *    period = 10h
   *    now    = 15h (i.e. +5h)
   *    shift  = min(10h * .3, 5h * .5) = min(3h, 2.5h) = 2.5h
   *    return = 10h - 2.5h = 7.5h
   *
   * 2. expirationH < 0, that means that time hasn't come yet. E.g.:
   *    period = 10h
   *    now    = 5h (i.e. -5h)
   *    shift  = max(10h * .3, 5h * .5) = min(3h, 2.5h) = 2.5h
   *    return = 10 + 2.5h = 12.5h
   */
  return periodH + expirationShiftH * sign;
};

type TShift = { shiftInHours: number; isBigger: boolean };

// TODO: completely refactor
const isPassiveDeck = (word: TWord) => word.deckUid.endsWith('-passive');

export const calcShiftInHours = (
  exercise: TExercise,
  word: TWord,
  factor: number
): TShift => {
  const mode = isPassiveDeck(word) ? 'passive' : 'active';

  if (factor === FACTORS.INIT) {
    // the first time or the word was reseted
    return {
      shiftInHours: INIT_SHIFT_IN_HOURS[mode][exercise],
      isBigger: true,
    };
  }

  const status = orDie(word.status[exercise]);
  const distanceInHours = Math.max(
    MIN_DISTANCE_IN_HOURS[mode][exercise],
    calcDistanceInHours(status)
  );
  const shiftInHours = roundFloat(distanceInHours * factor);

  return { shiftInHours, isBigger: distanceInHours < shiftInHours };
};

const addRandomSeed = (value: number): number => value * _.random(0.9, 1.1);

export const calcNextProbe = (
  exercise: TExercise,
  word: TWord,
  action: TRepetitionAction
): string => {
  switch (action.type) {
    case TRepetitionActionType.reset:
      return now().add(RESET_SHIFT_IN_HOURS, 'hour').format();

    case TRepetitionActionType.shift: {
      const { shiftInHours } = calcShiftInHours(exercise, word, action.factor);
      return now().add(addRandomSeed(shiftInHours), 'hour').format();
    }

    case TRepetitionActionType.skip:
      throw new Error('Incorrect word action type');
  }
};
